import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
} from 'reactstrap';
import { toast } from 'react-toastify';
import ActionButton from '../../../../Components/Common/ActionButton';
import { crudeApi } from '../../../../helpers/crude_api_helper';

// POST /v1/Crude/ImportCrude — unlike Create/AddDetail/UpdateDetail, this is
// direct CRUD (no ChangedRequest/approval step): rows land in GetListCrude
// immediately on a 204. Multipart field name must be exactly `File`
// ([FromForm] File on the backend) — see api-crude-registration.md §6.
const IMPORT_ENDPOINT = '/v1/Crude/ImportCrude';

const ImportCrudeModal = ({ isOpen, toggle, onImportSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setSelectedFile(null);
    setFileError('');
    setSubmitting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCancel = () => {
    resetState();
    toggle();
  };

  const handleFileChange = e => {
    const file = e.target.files[0] || null;
    if (file && !/\.xlsx$/i.test(file.name)) {
      setSelectedFile(null);
      setFileError('Only .xlsx files are allowed');
      return;
    }
    setSelectedFile(file);
    setFileError('');
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setFileError('Please select a file to import');
      return;
    }
    setSubmitting(true);
    try {
      await crudeApi.postMultipart(IMPORT_ENDPOINT, { File: selectedFile });
      toast.success('Data imported successfully');
      resetState();
      onImportSuccess();
    } catch (error) {
      toast.error(`Import failed: ${error}`);
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} centered={true}>
      <ModalHeader toggle={handleCancel}>Import Excel</ModalHeader>
      <ModalBody>
        <Label for='crudeImportFile' className='form-label'>
          Select File <span className='text-danger'>*</span>
        </Label>
        <Input
          innerRef={fileInputRef}
          id='crudeImportFile'
          type='file'
          accept='.xlsx'
          invalid={!!fileError}
          onChange={handleFileChange}
        />
        {fileError && (
          <div className='invalid-feedback d-block'>{fileError}</div>
        )}
      </ModalBody>
      <ModalFooter>
        <ActionButton
          variant='secondary'
          icon='ri-close-line'
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant='primary'
          icon='ri-send-plane-line'
          onClick={handleSubmit}
          disabled={!selectedFile || submitting}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

ImportCrudeModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  onImportSuccess: PropTypes.func,
};

export default ImportCrudeModal;
